<template>
    <div class="give-main">
        <button type="button" class="close-btn" aria-label="Close" autofocus :disabled="!!assigningId" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>

        <div class="header">
            <div class="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="24" fill="var(--success-wash)" />
                    <path d="M20 24L22.5 26.5L28 21M16 32H32C33.1046 32 34 31.1046 34 30V18C34 16.8954 33.1046 16 32 16H16C14.8954 16 14 16.8954 14 18V30C14 31.1046 14.8954 32 16 32Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </div>
            <h2 class="title">Assign a license to {{ contactName }}</h2>
            <!-- "You have" is the member talking about their own licenses. An admin acting for the
                 company is not the holder, and the count is not theirs, so the employer reading
                 gets the same number with no owner claimed — the footer says who pays. -->
            <p v-if="employerContext" class="subtitle">
                {{ availableLicenses.length }} license{{ availableLicenses.length === 1 ? '' : 's' }} available to
                assign to {{ contactName }}.
            </p>
            <p v-else class="subtitle">
                You have {{ availableLicenses.length }} license{{ availableLicenses.length === 1 ? '' : 's' }} available.
                Pick one to assign now, or purchase a new one.
            </p>
        </div>

        <div class="divider" />

        <div v-if="availableLicenses.length" class="license-list">
            <button
                v-for="license in pagedLicenses"
                :key="license.id"
                type="button"
                class="license-row"
                :disabled="!!assigningId"
                @click="assign(license)"
            >
                <div class="license-info">
                    <span class="license-plan">{{ planName(license) }}</span>
                    <span class="license-meta">{{ termLabel(license.term) }}</span>
                </div>
                <span v-if="assigningId === license.id" class="spinner" />
                <span v-else class="assign-cta">Assign</span>
            </button>

            <div v-if="totalPages > 1" class="pager">
                <button type="button" class="pager-btn" :disabled="page === 1 || !!assigningId" @click="page--">Previous</button>
                <span class="pager-status">Page {{ page }} of {{ totalPages }}</span>
                <button type="button" class="pager-btn" :disabled="page === totalPages || !!assigningId" @click="page++">Next</button>
            </div>
        </div>
        <template v-else>
            <!-- Says only what this modal read — the licenses available to assign here — and not
                 that the company's pool is empty, which it never looked at. -->
            <p v-if="employerContext && isRelative" class="empty-text" data-testid="employer-empty-state">
                No licenses are available to assign to {{ contactName }}. A company license is assigned to an
                employee from the company roster, so it cannot be given to a family member — that license is
                the employee's own to buy.
            </p>
            <p v-else-if="employerContext" class="empty-text" data-testid="employer-empty-state">
                No licenses are available to assign to {{ contactName }}. Buy company licenses in the business
                checkout, then assign one.
            </p>
            <p v-else-if="canPurchaseLicense" class="empty-text">You don't have any available licenses. Purchase one to assign to {{ contactName }}.</p>
            <div v-else class="gate-wrap">
                <EmployeePurchaseGate />
            </div>
        </template>

        <!-- In an employer context the recipient decides whether there is a purchase to offer at
             all: a company license reaches the employee, never their relative. Everywhere else the
             member's own gate decides, as before. -->
        <div v-if="employerContext ? !isRelative : canPurchaseLicense" class="footer">
            <p v-if="employerContext" class="payer-note" data-testid="company-payer-note">
                This is a company purchase: {{ employerContext.name }} is charged, on the company's payment method.
            </p>
            <PrimeButton
                variant="secondary"
                class="p-btn-root_secondary purchase-btn"
                :label="employerContext ? 'Buy company licenses' : 'Purchase a new license'"
                :disabled="!!assigningId"
                @click="purchaseNew"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { getDialogRef } from '~/composables/dynamic-dialog';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { useLicenseStore } from '~/stores/person/licenses';
import { LicenseRenewalTermDisplay, type LicenseRenewalTerm } from '~/types/licenses/licenses';
import { LICENSE_TIERS, licenseTierName } from '~/types/licenses/tiers';
import { ContactType } from '~/types/person/contacts';
import type { License } from '~/types/person/person';
import { ModalNames } from '~/types/modals';
import EmployeePurchaseGate from '~/components/main/person/licenses/EmployeePurchaseGate.vue';
import { checkoutAssignUrl } from '~/utils/cross-app-urls';
import { spareSeatsFor } from '~/utils/license-access';

const dialogRef = getDialogRef(ModalNames.GIVE_LICENSE);
const personStore = usePersonStore();
const contactsStore = useContactsStore();
const licenseStore = useLicenseStore();
const route = useRoute();

// Assigning an already-owned license is always allowed; only the "purchase a
// new license" affordance (a Stripe redirect) is hidden when the employee's
// company has disabled self-purchase.
//
// `employerContext` is the company this viewer is acting FOR, or null when they are acting as
// themselves. It decides the whole purchase half of this modal: who is quoted, who is charged, and
// which checkout the button opens, so the employer branches are taken first everywhere it appears.
// `canPurchaseLicense` answers "may this actor buy at all" for whoever is buying — the company's
// `write financial` grant for an employer, the employee self-purchase flag for a member — so the
// employer reading of this modal stays on screen for an admin who may spend, and an admin who may
// not keeps the company copy and is told why on the press rather than left a dead button.
const { canPurchaseLicense, employerContext, redirectEmployerPurchase, refuseEmployerFamilyPurchase } =
    useLicensePurchase();

const assigningId = ref<number | null>(null);

const contact = computed(() => dialogRef.value?.data?.contact);
const contactName = computed(() => contact.value?.name ?? 'this family member');

// Whether this modal is about someone OTHER than the loaded person. It decides the employer half
// of the page: a company license reaches an employee and not their relative, so for a relative
// there is no company purchase to offer and no company card to name. The employee's own row in
// their family list is a CONTACT like any other and is marked `is_self`, so it is not one.
const isRelative = computed(() => contact.value?.type === ContactType.CONTACT && !contact.value?.is_self);

// Any license the user owns that isn't assigned to anyone and hasn't expired — available to give.
// Opened from a locked feature, only the spares whose plan reaches it: handing over a lesser seat
// would leave the feature locked behind a seat the person now holds.
const feature = computed(() => dialogRef.value?.data?.feature);
const availableLicenses = computed<License[]>(() => spareSeatsFor(personStore.person, feature.value));

// Front-end pagination: show at most 5 licenses per page.
const PAGE_SIZE = 5;
const page = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(availableLicenses.value.length / PAGE_SIZE)));
const pagedLicenses = computed(() =>
    availableLicenses.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE),
);
// Clamp the page if the list shrinks (e.g. after assigning one and the list refreshes).
watch(totalPages, (t) => { if (page.value > t) page.value = t; });

const termLabel = (term: LicenseRenewalTerm) =>
    (LicenseRenewalTermDisplay as Record<number, string>)[term] ?? '';

// A catalog tier is named by the tier map, so the plan reads the same here as on the
// badge. The catalog also mints licenses outside the tier ladder (an ephemeral business
// license), and the API's `display` is the only name those have.
const planName = (license: License) =>
    LICENSE_TIERS[license.slug] ? licenseTierName(license.slug) : license.display || license.slug;

const close = () => {
    if (assigningId.value) return;
    dialogRef.value?.close();
};

const assign = async (license: License) => {
    if (assigningId.value || !contact.value) return;
    assigningId.value = license.id;
    try {
        // The store assigns from the loaded person's seats, refreshes them, and toasts either way.
        // On failure keep the modal open so the giver can retry.
        if (!await licenseStore.assignLicense(license.id, contact.value)) return;
        await contactsStore.fetchContacts();
        dialogRef.value?.close();
    } finally {
        assigningId.value = null;
    }
};

const purchaseNew = () => {
    if (!contact.value) return;
    dialogRef.value?.close();
    // An admin acting for the company buys company licenses in the business checkout, on the
    // company's payment method. This button built its own consumer redirect rather than going
    // through useLicensePurchase, which is how it stayed the one purchase surface the employer
    // guard did not cover; it asks the same guards now.
    //
    // The first is why this modal has two employer readings. Opened for the loaded EMPLOYEE (the
    // locked-feature gates' needs_gift), a company purchase serves them exactly. Opened for a
    // family member, it cannot: a company license is assigned from the roster, and they are not
    // on it.
    if (refuseEmployerFamilyPurchase(contact.value)) return;
    // The locked feature this picker was opened from travels with the purchase, the same way it
    // narrows `availableLicenses` above: without it the business checkout opens its plan chooser,
    // which leads with Pro, and a Signature-only feature stays locked behind whatever is bought.
    if (redirectEmployerPurchase(feature.value)) return;
    // Preserve the originating route (impersonation `?person`, non-/family surfaces) in return_to,
    // matching the GIFT branch in useLicensePurchase rather than hard-coding /family.
    window.location.href = checkoutAssignUrl({ id: contact.value.id, name: contact.value.name }, route.fullPath);
};
</script>

<style lang="scss" scoped>
.give-main {
    @apply relative sm:max-w-xl sm:w-[440px] w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}

.close-btn {
    @apply absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-zinc-100 disabled:opacity-50 z-10;
    color: var(--ink-muted);

    &:hover {
        color: var(--ink);
    }
}

.empty-text {
    @apply px-6 text-gray-600 text-sm font-normal leading-5;
}

.gate-wrap {
    @apply px-6;
}

.header {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;

    .icon {
        @apply mb-2;
        color: var(--success-mark);
    }
    .title {
        @apply text-gray-900 text-lg not-italic font-semibold leading-7;
    }
    .subtitle {
        @apply text-gray-600 text-sm not-italic font-normal leading-5;
    }
}

.divider {
    @apply h-px bg-zinc-300 my-4 mx-6;
}

.license-list {
    @apply flex flex-col gap-2 px-6;
}

.license-row {
    @apply flex items-center justify-between w-full rounded-lg border border-zinc-200 bg-white px-4 py-3 text-left transition;
    @apply hover:border-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60;

    .license-info {
        @apply flex flex-col;
    }
    .license-plan {
        @apply text-gray-900 text-sm font-semibold leading-5 capitalize;
    }
    .license-meta {
        @apply text-gray-500 text-xs font-normal leading-5;
    }
    .assign-cta {
        @apply text-blue-600 text-sm font-semibold leading-5;
    }
}

.pager {
    @apply flex items-center justify-between pt-2;

    .pager-btn {
        @apply text-blue-600 text-sm font-semibold leading-5 transition hover:text-blue-700 disabled:text-zinc-300 disabled:cursor-not-allowed;
    }
    .pager-status {
        @apply text-gray-500 text-xs font-normal leading-5;
    }
}

.footer {
    @apply flex flex-col justify-center items-stretch self-stretch p-6 pt-6 gap-3;

    .payer-note {
        @apply text-sm font-normal leading-5;
        color: var(--ink-muted);
    }

    .purchase-btn {
        @apply w-full;
    }
}

.spinner {
    @apply w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin;
}
</style>
